import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { AdminTabsComponent } from './ui/admin-tabs/admin-tabs.component';
import { AdminTabWheelComponent } from './ui/admin-tab-wheel/admin-tab-wheel.component';
import { AdminTabClientComponent } from './ui/admin-tab-client/admin-tab-client.component';
import { AdminTabBrandComponent } from './ui/admin-tab-brand/admin-tab-brand.component';
import { CreateWheelModalComponent } from './ui/create-wheel-modal/create-wheel-modal.component';
import { CreateClientModalComponent } from './ui/create-client-modal/create-client-modal.component';
import {
  createWheelForUser,
  loadBrand,
  loadClients,
  loadUserWheels,
  selectBrand,
  selectClients,
  selectPersonalError,
  selectUserWheels,
  setActiveWheel,
  setMode,
} from '../../state';
import { Store } from '@ngrx/store';
import { skip, take, tap } from 'rxjs';
import { selectAccountId } from '../../state/auth/auth.selectors';
import { loadCurrentUser } from '../../state/auth/auth.actions';
import { NEW_WHEEL_NAME } from '../wheel/features/wheel-name-form/wheel-name-form.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { mapActiveWheel } from '../../utils/mappers/active-wheel.mapper';

@Component({
  selector: 'wil-professional',
  standalone: true,
  imports: [CommonModule, AdminTabsComponent, AdminTabWheelComponent, AdminTabClientComponent, AdminTabBrandComponent],
  templateUrl: './professional.component.html',
  styleUrls: ['./professional.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfessionalComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  public readonly savedWheels$ = this.store.select(selectUserWheels);
  public readonly clientsError$ = this.store.select(selectPersonalError);
  public readonly brand$ = this.store.select(selectBrand);
  public readonly clients$ = this.store.select(selectClients).pipe(
    tap(() => {
      this.isLoadingClients.set(false);
    })
  );

  public readonly activeTab = signal<AdminTab>('wheels');
  public accountId: string | null = null;

  public readonly isLoadingClients = signal(false);

  ngOnInit() {
    this.store
      .select(selectAccountId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => {
        this.accountId = id || null;
      });

    // 1. direct access
    // 2. redirect
    this.store.dispatch(loadCurrentUser());
    this.store.dispatch(loadUserWheels());
    this.store.dispatch(loadBrand());

    // Load clients with error handling
    this.loadClients();
  }

  public onTabChange(tab: AdminTab): void {
    this.activeTab.set(tab);
  }

  public onCreateWheel(): void {
    this.store
      .select(selectUserWheels)
      .pipe(take(2), skip(1))
      .subscribe((wheels) => {
        const wheel = mapActiveWheel(wheels);

        if (!wheel) return;

        this.store.dispatch(setActiveWheel({ wheel: wheel }));
        this.store.dispatch(setMode({ mode: 'edit' }));

        this.dialog.open(CreateWheelModalComponent, {
          width: '98vw',
          maxHeight: '98vh',
          disableClose: false,
          hasBackdrop: true,
          panelClass: 'create-wheel-modal-panel',
          data: { wheelId: wheel?.id, name: wheel?.name },
        });
      });

    this.store.dispatch(
      createWheelForUser({
        name: NEW_WHEEL_NAME,
        categoryIds: [],
        isDefault: true,
      })
    );
  }

  public loadClients(): void {
    this.isLoadingClients.set(true);
    this.store.dispatch(loadClients());
  }

  public onCreateClient(): void {
    this.dialog.open(CreateClientModalComponent, {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'create-client-modal-panel',
      data: { mode: 'create' },
    });
  }
}
